import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  Observable,
  catchError,
  combineLatest,
  finalize,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { authRoutesNames } from 'src/core/routes/auth/auth.routes.names';
import { coreRoutesNames } from 'src/core/routes/core.routes.names';
import { AuthStore } from 'src/core/stores';

@Component({
  selector: 'core-verify-registration',
  standalone: true,
  imports: [FormsModule, CommonModule, TranslocoDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './verify-registration.component.html',
})
export class VerifyRegistrationComponent {
  private route = inject(ActivatedRoute);
  private authStore = inject(AuthStore);
  private router = inject(Router);

  isLinkExpired$: Observable<boolean>;

  constructor() {
    this.authStore.setIsLoading(true);

    this.isLinkExpired$ = combineLatest([
      this.route.paramMap,
      this.route.queryParamMap,
    ]).pipe(
      switchMap(([pathParams, queryParams]) => {
        const { uuid, token } = this.extractParams(pathParams, queryParams);

        if (!uuid || !token) {
          return this.handleError(
            <HttpErrorResponse>new Error('Missing parameters'),
          );
        }

        return this.authStore.verifyByToken(uuid, token).pipe(
          tap(() => this.authStore.setIsLoading(false)),
          switchMap(() => of(false)), // if successful, return false for not expired
          catchError((error) => this.handleError(error)),
          finalize(() => this.authStore.setIsLoading(false)),
        );
      }),
      catchError((error) => this.handleError(error, true)),
    );
  }

  extractParams(
    pathParams: ParamMap,
    queryParams: ParamMap,
  ): { uuid: string | null; token: string | null } {
    const uuid = pathParams.get('uuid');
    const token = queryParams.get('token');
    return { uuid, token };
  }

  handleError(
    error: HttpErrorResponse,
    returnExpired: boolean = false,
  ): Observable<boolean> {
    this.authStore.setIsLoading(false);
    console.error('Error during verification:', error);
    if (error.status === 401) {
      console.error('Link has expired:', error);
      return of(true);
    }
    return returnExpired
      ? of(true)
      : throwError(
          () => new Error('Error during verification: ' + error.message),
        );
  }

  goToRegistrationPage() {
    this.router.navigate([coreRoutesNames.AUTH, authRoutesNames.REGISTER]);
  }

  goHome() {
    this.router.navigate(['/']);
  }
}
